import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { isClient, getSessionStorageItem, getCookie } from '@pegasus/utils';
// eslint-disable-next-line import/no-unresolved
import { deleteCookie, setCookie } from '@client/helpers/cookie';
import { getPropertiesFromBrazeCard, CardMethodsFromBraze, ClassicCard } from '@pegasus/braze';
import { Message, MessagePurpose, MessageType } from '@lux/message';
import { trackSelectContent, trackViewContent } from '@browsing/tracking';
import { ErrorBoundary } from '@browsing/components';
import { removeSessionStorageItem, setTodayDateAtMidnight } from '../helpers';

interface Props {
    cardToRender: ClassicCard;
    cardMethodsFromBraze: CardMethodsFromBraze;
}

export const BrazeBanner: React.FC<Props> = ({ cardToRender, cardMethodsFromBraze }) => {
    const [showCard, setShowCard] = useState(false);

    const itemCard = getPropertiesFromBrazeCard(cardToRender);

    const dismissedCardIdFromSessionStorage =
        isClient() && getSessionStorageItem('dismissed_braze_card_id_for_session');
    // TODO: this as method to ensure that we show the banner only for desired dismissed card
    useEffect(() => {
        const isBrazeAlreadyRedeem = getCookie('is_braze_experience_checkout') === 'true';
        if (
            dismissedCardIdFromSessionStorage &&
            dismissedCardIdFromSessionStorage === cardToRender.id
        ) {
            setShowCard(!isBrazeAlreadyRedeem);
        }
    }, [dismissedCardIdFromSessionStorage, cardToRender.id]);

    const footNote = itemCard.extras?.footNote;

    const { logCardClick } = cardMethodsFromBraze;

    const trackImpression = (inView: boolean) => {
        if (inView) {
            trackViewContent('braze_inline_banner', 'view');
        }
    };

    const todayDateAtMidnight = setTodayDateAtMidnight();
    const handleCtaClickAndCommunicateToBraze = () => {
        setShowCard(false);
        logCardClick(cardToRender, true);
        trackSelectContent('braze_inline_banner', 'click', { contentId: 'redeem' });
        if (isClient()) {
            removeSessionStorageItem('brazeCardToRender');
            removeSessionStorageItem('dismissed_braze_card_id_for_session');
            removeSessionStorageItem('show_braze_voucher_dismissed');
        }

        deleteCookie('show_braze_banner');

        // @ts-expect-error
        setCookie('is_braze_experience_checkout', 'true', todayDateAtMidnight.toUTCString(), '/');

        window.location.assign(`${itemCard.url}&brazeVoucher=true`);
    };

    const safelyTruncatedTitle =
        itemCard.title.length > 80 ? `${itemCard.title.slice(0, 80)}...` : itemCard.title;

    return (
        showCard && (
            <ErrorBoundary componentName="BrazeBanner">
                <InView
                    as="div"
                    onChange={inView => trackImpression(inView)}
                    triggerOnce
                    threshold={0.9}
                >
                    <Message
                        type={MessageType.INLINE}
                        purpose={MessagePurpose.COMMERCIAL}
                        title={safelyTruncatedTitle}
                        onLinkClick={handleCtaClickAndCommunicateToBraze}
                        linkText={itemCard.ctaText}
                        footerText={footNote}
                    >
                        {itemCard.message}
                    </Message>
                </InView>
            </ErrorBoundary>
        )
    );
};
